import moment from "moment";
import jwtDecode from "jwt-decode";
import { Utility } from "../../utils";
import { Log } from "../../utils/Log";

export interface UserTokenData {
    isAdmin: boolean;
    isMember: boolean;
    legacyMemberGuid?: string;
    legacyUserGuid?: string;
    requireSessionRefresh: boolean;
    requiresNewAccount: boolean;
    username?: string;
    userFullName?: string;
    userFirstName?: string;
    userMiddleName?: string;
    userLastName?: string;
    userGuid?: string;
    userId?: number;
    userProfilePicture?: string;
    userTitle?: string;
}

export interface AuthTokenData extends UserTokenData {
    clientId: string;
    clientName: string;
    expiresOnUTC: number;
    issuedOnUTC: number;
    refreshExpiresOnUTC: number;
    hasUserRecord: boolean;
    altUser?: UserTokenData;
}

export const DefaultAuthToken: AuthTokenData = {
    isAdmin: false,
    isMember: false,
    clientId: "",
    clientName: "",
    expiresOnUTC: 0,
    issuedOnUTC: 0,
    refreshExpiresOnUTC: 0,
    requireSessionRefresh: false,
    requiresNewAccount: false,
    hasUserRecord: false,
};

/**
 * Represents the raw access token data after decoded.
 */
export interface RawAccessTokenData {
    /** Audience */
    aud: string;

    /** Client name */
    cln: string;

    /** Has alt user data (admin) */
    hau: boolean;

    /** Has admin role */
    har: boolean;

    /** Has member role */
    hmr: boolean;

    /** Has user record */
    hur: boolean;

    /** Issued at UTC (Unix Epoch) */
    iat: number;

    /** Expires at UTC (Unix Epoch) */
    exp: number;

    /** Refresh token expires at UTC (Unix Epoch) */
    rexp: number;

    /** Issuer */
    iss: string;

    /** Unique jwt ket */
    jti: string;

    /** Subject (Client ID) */
    sub: string;

    /** Legacy member GUID */
    lmgd?: string;

    /** Legacy user GUID */
    lugd?: string;

    /** Username */
    email?: string;

    /** User GUID */
    serialnumber?: string;

    /** User ID */
    uid?: number;

    /** User profile picture */
    ava?: string;

    /** User full name */
    name?: string;

    /** User first name */
    ufn?: string;

    /** User middle name */
    umn?: string;

    /** User last name */
    uln?: string;

    /** User title */
    utt?: string;

    /** Requires session refresh */
    reqsr: boolean;

    /** Requires new account */
    reqna: boolean;

    /****************
    * Alt user data *
    *****************/

    /** Alt Has admin role */
    a_har: boolean;

    /** Alt Has member role */
    a_hmr: boolean;

    /** Alt Username */
    a_email?: string;

    /** Alt User full name */
    a_name?: string;

    /** Alt User GUID */
    a_serialnumber?: string;

    /** Alt User ID */
    a_uid?: number;

    /** Alt User profile picture */
    a_ava?: string;

    /** Alt User first name */
    a_ufn?: string;

    /** Alt User middle name */
    a_umn?: string;

    /** Alt User last name */
    a_uln?: string;
}

export interface TokenAuthorizationResult {
    access_token: string;
    token_type: string;
    expires_in: number;
    refresh_token: string;
    refresh_token_expires: Date;
    [".expires"]: Date;
    [".issued"]: Date;
}

export class AuthTokenDecoded {
    // interface implementations
    public accessToken: string = "";
    public refreshToken: string = "";
    public tokenData?: AuthTokenData = undefined;

    public isValid: boolean = false;

    constructor(accessToken: string, refreshToken: string) {
        try {
            this.accessToken = accessToken;
            this.refreshToken = refreshToken;
            this.tokenData = this.getAuthTokenData();
            this.isValid = true;
        }
        catch (error) {
            this.isValid = false;
            Log.error(`Unable to decode access token ${accessToken} due to error: ${error}`);
        }
    }

    /**
     * Returns alt user token data
     */
    public getAltUserData(): UserTokenData | undefined {
        try {
            return this.getAuthTokenData().altUser;
        }
        catch (error) {
            return undefined;
        }
    }

    /**
     * Returns true if user record within access token has an admin role.
     */
    public hasAdminRole(): boolean {
        try {
            const data = this.getAuthTokenData();
            return data.isAdmin;
        }
        catch (error) {
            return false;
        }
    }

    /**
     * Returns true if user record within access token has a member role.
     */
    public hasMemberRole(): boolean {
        try {
            const data = this.getAuthTokenData();
            return data.isMember;
        }
        catch (error) {
            return false;
        }
    }

    /**
     * @returns {boolean} Whether or not the token is expired
     */
    public isExpired(): boolean {
        if (this.tokenData === undefined)
            return true;

        const now = new Date(),
            expires = Utility.utcToLocal(moment.unix(this.tokenData.expiresOnUTC).toDate()),
            msDiff = expires.getTime() - now.getTime();

        return msDiff < 0;
    }

    /**
     * @returns {boolean} Whether or not the token is expired
     */
    public isExpiredRefreshToken(): boolean {
        if (this.tokenData === undefined)
            return true;

        const now = new Date(),
            expires = Utility.utcToLocal(moment.unix(this.tokenData.refreshExpiresOnUTC).toDate()),
            msDiff = expires.getTime() - now.getTime();

        return msDiff < 0;
    }

    private getAuthTokenData(): AuthTokenData {
        const jwtObj = jwtDecode<RawAccessTokenData>(this.accessToken);

        const altUser: UserTokenData | undefined = !jwtObj.hau
            ? undefined
            : {
                isAdmin: jwtObj.a_har,
                isMember: jwtObj.a_hmr,
                requireSessionRefresh: false,
                requiresNewAccount: false,
                username: jwtObj.a_email,
                userFullName: jwtObj.a_name,
                userFirstName: jwtObj.a_ufn,
                userMiddleName: jwtObj.a_umn,
                userLastName: jwtObj.a_uln,
                userGuid: jwtObj.a_serialnumber,
                userId: jwtObj.a_uid,
                userProfilePicture: jwtObj.a_ava,
            };

        return {
            clientId: jwtObj.sub,
            clientName: jwtObj.cln,
            issuedOnUTC: jwtObj.iat,
            expiresOnUTC: jwtObj.exp,
            refreshExpiresOnUTC: jwtObj.rexp,
            hasUserRecord: jwtObj.hur,
            isAdmin: jwtObj.har,
            isMember: jwtObj.hmr,
            legacyMemberGuid: jwtObj.lmgd,
            legacyUserGuid: jwtObj.lugd,
            requireSessionRefresh: jwtObj.reqsr,
            requiresNewAccount: jwtObj.reqna,
            username: jwtObj.email,
            userFullName: jwtObj.name,
            userFirstName: jwtObj.ufn,
            userMiddleName: jwtObj.umn,
            userLastName: jwtObj.uln,
            userId: jwtObj.uid,
            userGuid: jwtObj.serialnumber,
            userProfilePicture: jwtObj.ava,
            userTitle: jwtObj.utt,
            altUser,
        };
    }

}



// WEBPACK FOOTER //
// ./src/models/auth/AuthToken.ts